import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
import moment from 'moment';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';

import { getProjects } from 'services/project';
import {
  startYearlyReport,
  getYearlyReportJob,
  getLastYearlyReportJob,
  getYearlyReport,
} from 'services/yearlyReport';
import { formatDateTime } from 'services/date';

// core components
import GridItem from 'components/Grid/GridItem';
import GridContainer from 'components/Grid/GridContainer';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import Button from 'components/CustomButtons/Button';

import { ConsoleContext } from 'services/context';
import YearlyClientReport from './components/YearlyClientReport';
import YearlyPayrollReport from './components/YearlyPayrollReport';

const POLLING_INTERVAL = 5000; // 5 seconds

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 120,
    marginRight: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  buttonWrapper: {
    position: 'relative',
  },
  infoText: {
    marginLeft: '15px',
    display: 'inline-block',
  },
}));

export default function YearlyReport() {
  const classes = useStyles();
  const { showError, showLoader, t } = useContext(ConsoleContext);

  const [year, setYear] = useState(moment().year());
  const [projects, setProjects] = useState([]);
  const [fluviusId, setFluviusId] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [lastReportDate, setLastReportDate] = useState(null);

  const years = Array.from(new Array(5), (val, index) => moment().year() - index);

  const checkJobStatus = useCallback(async (jobId) => {
    try {
      const job = await getYearlyReportJob(jobId);

      if (job.failedAt) {
        setIsGenerating(false);
        showError(new Error('Report generation failed'));
        return;
      }

      // If job has lastFinishedAt, it means it's completed
      if (job.lastFinishedAt) {
        showLoader(true);
        try {
          const report = await getYearlyReport(year);
          setReportData(report);
        } catch (reportError) {
          console.warn('Report not ready yet, will retry');
          // Report might not be ready immediately, continue polling
          setTimeout(() => checkJobStatus(jobId), POLLING_INTERVAL);
          showLoader(false);
          return;
        }
        setIsGenerating(false);
        showLoader(false);
        return;
      }

      // Continue polling if job is not finished
      setTimeout(() => checkJobStatus(jobId), POLLING_INTERVAL);
    } catch (error) {
      setIsGenerating(false);
      showLoader(false);
      showError(error);
    }
  }, [year, showError, showLoader]);

  // Fetch projects only once on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      showLoader(true);
      try {
        const projectsData = await getProjects();
        setProjects(projectsData);
      } catch (error) {
        showError(error);
      }
      showLoader(false);
    };

    fetchProjects();
  }, []); // Remove showError and showLoader from dependencies

  // Check for existing job/report when year changes
  useEffect(() => {
    const checkExistingJob = async () => {
      showLoader(true);
      try {
        const lastJob = await getLastYearlyReportJob(year);

        if (lastJob && !lastJob.failedAt) {
          if (lastJob.lastFinishedAt) {
            // Job is completed, set last report date and try to get the report
            setLastReportDate(lastJob.lastFinishedAt);
            try {
              const report = await getYearlyReport(year);

              if (report && (report.summaryProfit || report.payroll)) {
                setReportData(report);
              } else {
                setReportData(null);
              }
            } catch (reportError) {
              // Report doesn't exist yet, that's fine
              setReportData(null);
            }
          } else {
            // Job is still running, start polling
            setIsGenerating(true);
            checkJobStatus(lastJob._id);
          }
        } else {
          // No job found or job failed, try to get existing report
          try {
            const report = await getYearlyReport(year);

            if (report && (report.summaryProfit || report.payroll)) {
              setReportData(report);
            } else {
              setReportData(null);
            }
          } catch (reportError) {
            // Report doesn't exist yet, that's fine
            setReportData(null);
          }
        }
      } catch (error) {
        // Ignore errors when checking for existing job
        console.warn('Error checking existing job:', error);
        setReportData(null);
      }
      showLoader(false);
    };

    checkExistingJob();
  }, [year]);

  // Set fluvius ID when projects are loaded
  useEffect(() => {
    if (projects.length) {
      try {
        const fluvius = projects.find((p) => p.clientId?.companyName === 'Fluvius');
        if (fluvius) {
          setFluviusId(fluvius.clientId._id);
        }
      } catch (error) {
        console.warn('Error finding Fluvius client:', error);
      }
    }
  }, [projects]);

  const handleYearChange = (event) => {
    const newYear = event.target.value;
    setYear(newYear);
    setReportData(null);
    setIsGenerating(false);
    setLastReportDate(null);
  };

  const handleCreateReport = async () => {
    try {
      setIsGenerating(true);
      const { job } = await startYearlyReport(year);
      checkJobStatus(job._id);
    } catch (error) {
      setIsGenerating(false);
      showError(error);
    }
  };

  const renderLastReportDate = (lastReportDate) => {
    if (!lastReportDate) {
      return 'No info';
    }

    return formatDateTime(lastReportDate);
  };

  return (
    <>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardBody>
              <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                  <div className={classes.controls}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>{t('Year')}</InputLabel>
                      <Select
                        value={year}
                        onChange={handleYearChange}
                      >
                        {years.map((yearOption) => (
                          <MenuItem key={yearOption} value={yearOption}>
                            {yearOption}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <div className={classes.buttonWrapper}>
                      <Button
                        color="primary"
                        onClick={handleCreateReport}
                        disabled={isGenerating}
                      >
                        {isGenerating ? t('Generating...') : t('Create Report')}
                      </Button>
                      {isGenerating && (
                        <CircularProgress size={24} className={classes.buttonProgress} />
                      )}
                    </div>
                    <Alert icon={false} severity="info" className={classes.infoText}>
                      Last Generated:
                      {' '}
                      {renderLastReportDate(lastReportDate)}
                    </Alert>
                  </div>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {reportData?.summaryProfit?.monthlyRecords && (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="success">
                <h4>{t('PP Report')}</h4>
              </CardHeader>
              <CardBody>
                <YearlyClientReport
                  summary={reportData.summaryProfit.monthlyRecords}
                  projects={projects}
                  excludeClientId={fluviusId}
                />
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      )}

      {reportData?.payroll?.monthlyRecords && (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="info">
                <h4>{t('Payroll Report')}</h4>
              </CardHeader>
              <CardBody>
                <YearlyPayrollReport payrollReport={reportData.payroll.monthlyRecords} />
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      )}
    </>
  );
}
